import React, { useRef, useState } from 'react';
import { ArrowLeft16, ArrowRight16 } from '@carbon/icons-react';

interface UseOverscrollNavigationProps {
  onScrollPastEnd?: () => void;
  onScrollPastStart?: () => void;
  showPreviousOverscroll?: boolean;
  showNextOverscroll?: boolean;
  scrollRef?: React.RefObject<HTMLDivElement>; // Optional for scroll-aware behavior
}

interface OverscrollState {
  direction: 'left' | 'right' | null;
  amount: number;
  active: boolean;
}

interface UseOverscrollNavigationReturn {
  overscrollState: OverscrollState;
  touchHandlers: {
    onTouchStart: (e: React.TouchEvent<HTMLDivElement>) => void;
    onTouchMove: (e: React.TouchEvent<HTMLDivElement>) => void;
    onTouchEnd: () => void;
  };
  OverscrollIndicators: React.FC;
}

const useOverscrollNavigation = ({
  onScrollPastEnd,
  onScrollPastStart,
  showPreviousOverscroll = false,
  showNextOverscroll = false,
  scrollRef
}: UseOverscrollNavigationProps): UseOverscrollNavigationReturn => {
  const [overscrollState, setOverscrollState] = useState<OverscrollState>({
    direction: null,
    amount: 0,
    active: false
  });

  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  const scrollStartLeft = useRef(0);
  const isAtEnd = useRef(false);
  const isAtStart = useRef(false);
  const overscrollThreshold = 50; // Pixels to trigger action

  // Check scroll boundaries - for components with scrollable content
  const checkScrollBoundaries = () => {
    if (scrollRef?.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
      const atStart = scrollLeft <= 1;

      isAtEnd.current = atEnd;
      isAtStart.current = atStart;
    } else {
      // For non-scrollable content, always allow overscroll
      isAtEnd.current = true;
      isAtStart.current = true;
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchCurrentX.current = touch.clientX;

    if (scrollRef?.current) {
      scrollStartLeft.current = scrollRef.current.scrollLeft;
    }

    // Check boundaries at start of touch
    checkScrollBoundaries();

    // Reset overscroll state
    setOverscrollState({ direction: null, amount: 0, active: false });
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    touchCurrentX.current = touch.clientX;
    const deltaX = touch.clientX - touchStartX.current;

    // Re-check boundaries during move (in case scroll position changed)
    checkScrollBoundaries();

    // Check if we're trying to overscroll
    if (isAtEnd.current && deltaX < -20) {
      // Trying to scroll right when at end (swipe left)
      const overscrollAmount = Math.min(Math.abs(deltaX) - 20, 100);
      setOverscrollState({
        direction: 'right',
        amount: overscrollAmount,
        active: overscrollAmount >= overscrollThreshold
      });

      // Prevent default scrolling behavior during overscroll
      e.preventDefault();
    } else if (isAtStart.current && deltaX > 20) {
      // Trying to scroll left when at start (swipe right)
      const overscrollAmount = Math.min(deltaX - 20, 100);
      setOverscrollState({
        direction: 'left',
        amount: overscrollAmount,
        active: overscrollAmount >= overscrollThreshold
      });

      // Prevent default scrolling behavior during overscroll
      e.preventDefault();
    } else {
      // Normal scrolling or cancelled overscroll
      setOverscrollState({ direction: null, amount: 0, active: false });
    }
  };

  const handleTouchEnd = () => {
    const { direction, active } = overscrollState;

    if (active) {
      // Trigger navigation based on direction
      if (direction === 'right') {
        onScrollPastEnd?.();
      } else if (direction === 'left') {
        onScrollPastStart?.();
      }
    }

    // Reset overscroll state
    setOverscrollState({ direction: null, amount: 0, active: false });
  };

  const touchHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd
  };

  // Component for rendering overscroll indicators
  const OverscrollIndicators: React.FC = () => (
    <>
      {/* Left overscroll indicator */}
      {showPreviousOverscroll &&
        overscrollState.direction === 'left' &&
        overscrollState.amount > 0 && (
          <div
            className={`mobile-view-overscroll-indicator mobile-view-overscroll-left ${
              overscrollState.active ? 'mobile-view-overscroll-active' : ''
            }`}
            style={{
              width: `${Math.min(
                (overscrollState.amount / overscrollThreshold) * 80,
                80
              )}px`
            }}
          >
            <ArrowLeft16
              fill={overscrollState.active ? '#FFFFFF' : '#000000'}
            />
          </div>
        )}

      {/* Right overscroll indicator */}
      {showNextOverscroll &&
        overscrollState.direction === 'right' &&
        overscrollState.amount > 0 && (
          <div
            className={`mobile-view-overscroll-indicator mobile-view-overscroll-right ${
              overscrollState.active ? 'mobile-view-overscroll-active' : ''
            }`}
            style={{
              width: `${Math.min(
                (overscrollState.amount / overscrollThreshold) * 80,
                80
              )}px`
            }}
          >
            <ArrowRight16
              fill={overscrollState.active ? '#FFFFFF' : '#000000'}
            />
          </div>
        )}
    </>
  );

  return {
    overscrollState,
    touchHandlers,
    OverscrollIndicators
  };
};

export default useOverscrollNavigation;
